import { Component, Input, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs'
import { ReactiveFormsModule } from '@angular/forms'
import { FormBuilder } from '@angular/forms'
import {
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table'
import { MatIcon } from '@angular/material/icon'
import { NgIf, AsyncPipe, DatePipe } from '@angular/common'
import { Title } from '@angular/platform-browser'
import { NgxPaginationModule } from 'ngx-pagination'
import { Filter, QueryGet } from '../../../shared/query-types'
import { Pagination } from '../../../models/pagination.interface'
import { TrackingService } from '../../../services/tracking/tracking.service'
import { Tracking } from '../../../models/tracking.interface'
import { ToastrService } from 'ngx-toastr'
import { FormGroup } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core'
import { MatSelectModule } from '@angular/material/select'
import { LayoutService } from '../../../services/layout/layout.service'
import { PAGINATION } from '../../../shared/constants'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatInput } from '@angular/material/input'
import { UploadfileService } from '../../../services/uploadfile/uploadfile.service'
import { OnlysuperuserDirective } from '../../../directives/onlysuperuser.directive'
import { MatDialog } from '@angular/material/dialog'
import { TrackingFormComponent } from '../tracking-form/tracking-form.component'

@Component({
    selector: 'app-trackings',
    standalone: true,
    imports: [
        NgIf,
        MatIcon,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        NgxPaginationModule,
        AsyncPipe,
        DatePipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatOptionModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatInput,
        OnlysuperuserDirective,
    ],
    templateUrl: './trackings.component.html',
    styleUrl: './trackings.component.css',
})
export class TrackingsComponent implements OnInit {
    public page: number | undefined
    @Input() filtro: string = ''
    @Input() orden: string = ''
    limit = 10
    total = 0
    asyncTracking!: Observable<Pagination<Tracking[]>>
    trackingList: Tracking[] = []
    filterForm!: FormGroup

    showColumns: string[] = [
        'acciones',
        'date',
        'affectationKind',
        'affectationScope',
        'affectationDetail',
        'commitments',
        'supportKind',
        'supportDetail',
        'remainingSupport',
        'file',
    ]

    constructor(
        private router: Router,
        private title: Title,
        private trackingService: TrackingService,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private layoutService: LayoutService,
        private activatedRouter: ActivatedRoute,
        private uploadfileService: UploadfileService,
        private dialog: MatDialog
    ) {
        this.title.setTitle('Seguimientos')
    }

    ngOnInit(): void {
        this.filterForm = this.fb.group({
            date: [''],
        })

        this.fetchTracking(1)
        this.filterForm.valueChanges.subscribe(() => {
            this.onFilter()
        })

        this.layoutService.paginationLimit$
            .pipe(
                switchMap((limit) => {
                    this.limit = limit
                    return this.activatedRouter.queryParamMap
                }),
                switchMap((params) => {
                    const pageNum = params.get('page') || '1'
                    const search = params.get('search') || ''
                    this.page = parseInt(pageNum)
                    this.fetchTracking(PAGINATION.first_page, null, search)
                    return this.layoutService.paginationLimit$
                })
            )
            .subscribe()

        this.trackingService.trackings$.subscribe(
            (tracking) => (this.trackingList = tracking)
        )
    }

    fetchTracking(
        page: number,
        filter: Filter | null = null,
        search = ''
    ): void {
        const offset = page > 1 ? page * this.limit - this.limit : 0
        const params: QueryGet = {
            order: '-id',
            limit: this.limit,
            offset,
            search,
            filters: [{ name: 'isActive', value: 1 }],
        }

        if (filter) {
            params.filters.push(filter)
        }
        this.page = page
        this.asyncTracking = this.trackingService.allTrackings(params).pipe(
            tap((response) => {
                this.trackingList = response.results
                this.trackingService.setTrackings(response.results)
                this.total = response.count
            }),
            map((res) => {
                return res
            }),
            catchError((error) => {
                console.error('Error cargando seguimientos:', error)
                this.router.navigate(['home/'])
                return []
            })
        )
    }

    openFile(tracking: Tracking): void {
        const id = tracking?.fileAttach?.fileUrl
        const name = tracking?.fileAttach?.fileName || 'archivo'
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se pudo descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    hasFile(tracking: Tracking) {
        return (
            !!tracking?.fileAttach?.fileUrl && !!tracking?.fileAttach?.fileName
        )
    }

    editTracking(tracking: Tracking) {
        this.trackingService.setSelectedTracking(tracking)

        const dialogRef = this.dialog.open(TrackingFormComponent, {
            width: '90vw',
            maxWidth: '80vw',
            maxHeight: '80vh',
            panelClass: 'custom-dialog-container',
            data: {
                isCreate: false,
            },
        })

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.fetchTracking(PAGINATION.first_page)
            }
        })
    }

    ExportTrackings() {
        this.toastr.info('Exportar aún no implementado')
    }

    onFilter(): void {
        const { date } = this.filterForm.value
        let filter: Filter | null = null

        if (date) {
            const formattedDate = new Date(date).toISOString().split('T')[0]
            filter = { name: 'date', value: formattedDate }
        }

        this.fetchTracking(PAGINATION.first_page, filter)
    }

    deleteTracking(trackingId: number) {
            if (confirm('¿Seguro que deseas eliminar este seguimiento?')) {
                this.trackingService
                    .deleteTracking(trackingId)
                    .pipe(
                        tap(() => {
                            const successMsg = `Eliminado CORRECTAMENTE!`
                            this.toastr.success(successMsg, 'Exitoso!', {
                                progressBar: true,
                                timeOut: 5000,
                            })
                            this.fetchTracking(PAGINATION.first_page)
                        }),
                        catchError((err) => {
                            console.error('Error eliminando seguimiento:', err)
                            const successMsg = `NO se pudo eliminar!`
                            this.toastr.error(successMsg, 'Error!', {
                                progressBar: true,
                                timeOut: 5000,
                            })
                            return of(null)
                        })
                    )
                    .subscribe()
            }
        }
}
